import React, { useEffect, useState } from "react";
import { TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import EditIcon from "@mui/icons-material/Edit";
import { ActionButton, H4 } from "components";
import styled from "styled-components";
import OverlayRightPanel from "containers/OverlayRightPanel/OverlayRightPanel.container";
import { useTypedSelector } from "store";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { productRegionOptions } from "store/reducers/products/products.types";
import { updateProductLineAction } from "store/reducers/products/products.actions";
import {
  getExportNameValidationError,
  getProductlineNameValidationError,
  MAX_PRODUCTLINE_NAME_LENGTH,
} from "utils/file.util";
import AddUserForm from "./AddUserForm";
import DeleteProductLineButton from "./DeleteProductLineButton";
import UserList from "./UserList";
import ExportProductlineToExcelButton from "./ActionButtons/ExportProductlineToExcelButton";
import ImportFromExcelButton from "./ActionButtons/ImportFromExcelButton/ImportFromExcelButton";
import { ValidateProductlineButton } from "./ActionButtons/ValidateProductlineButton";
import ActionStack from "components/ActionStack/ActionStack";
import { ShowValidationsButton } from "./ActionButtons/ShowValidationsButton";
import { ValidationSettingsButton } from "./ActionButtons/ValidationSettingsButton";

const NAME_FIELD_TOOLTIP =
  "Display name for this productline in dPDR. Also used for Excel export file naming. 50 characters max and must be unique within a region.";

const EXPORT_NAME_FIELD_TOOLTIP =
  'Filename used when exporting Rules Engine and Database JSON files. 50 characters max. Cannot include \\ / : * ? " < > |.';

const REGION_FIELD_TOOLTIP = "Name must be unique within a region.";

const DESCRIPTION_FIELD_TOOLTIP = "Optional description of the productline";

const ID_FIELD_TOOLTIP = "Unique identifier for this productline.";

const FAMILY_ID_FIELD_TOOLTIP =
  "Identifier linking all versions of this productline.";

export default function ProductOverview() {
  const dispatch = useDispatch();

  //State
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isWritePriv, setIsWritePriv] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [exportName, setExportName] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [showUsersSidePanel, setShowUsersSidePanel] = useState<boolean>(false);

  //Redux Store
  const { id: currentUserId } = useTypedSelector((state) => state.user);
  const {
    getting,
    selectedProductLine: productLine,
    users,
  } = useTypedSelector((state) => state.products);

  useEffect(() => {
    if (productLine) {
      //Determine whether the current user has admin rights on the productline.
      const currentUserIsAdmin = users.users.some(
        (user) =>
          user.id === currentUserId.toString().trim() && user.role === "ADMIN"
      );
      setIsAdmin(currentUserIsAdmin);

      //Determine whether the current user has admin or editor rights on the productline.
      const currentUserIsWritePriv = users.users.some(
        (user) =>
          user.id === currentUserId.toString().trim() &&
          (user.role === "ADMIN" || user.role === "EDITOR")
      );
      setIsWritePriv(currentUserIsWritePriv);

      setName(productLine.name);
      setExportName(productLine.exportName ?? productLine.name ?? "");
      setRegion(productLine.region);
      setDescription(productLine.description || "");
    } else {
      setIsAdmin(false);
      setName("");
      setExportName("");
      setRegion("");
      setDescription("");
    }
  }, [productLine, currentUserId, users]);

  //Handlers

  const handleNameChange = (event) => {
    setName(event.target.value);
  };

  const updateName = async () => {
    if (!productLine) return;

    const newName = name.trim();
    if (newName === productLine?.name) return;

    const validationError = getProductlineNameValidationError(newName);
    if (validationError) {
      toast.error(validationError);
      setName(productLine.name);
      return;
    }

    const success = await dispatch(
      updateProductLineAction(productLine.id, {
        name: newName,
      })
    );

    if (!success) {
      setName(productLine.name);
    }
  };

  const handleExportNameChange = (event) => {
    setExportName(event.target.value);
  };

  const updateExportName = async () => {
    if (!productLine) return;

    const newExportName = exportName.trim();
    const currentExportName = productLine.exportName ?? productLine.name ?? "";
    if (newExportName === currentExportName) return;

    const validationError = getExportNameValidationError(newExportName);
    if (validationError) {
      toast.error(validationError);
      setExportName(currentExportName);
      return;
    }

    const success = await dispatch(
      updateProductLineAction(productLine.id, {
        exportName: newExportName,
      })
    );

    if (!success) {
      setExportName(currentExportName);
    }
  };

  const handleRegionChange = async (event) => {
    if (!productLine) return;

    const newRegion = event.target.value;
    if (newRegion === productLine.region) return;

    const previousRegion = productLine.region;
    setRegion(newRegion);

    const success = await dispatch(
      updateProductLineAction(productLine.id, {
        region: newRegion,
      })
    );

    if (!success) {
      setRegion(previousRegion);
    }
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const updateDescription = async () => {
    if (!productLine) return;

    const newDescription = description.trim();
    if (newDescription === productLine?.description) return;

    const previousDescription = productLine.description || "";
    const success = await dispatch(
      updateProductLineAction(productLine.id, {
        description: newDescription,
      })
    );

    if (!success) {
      setDescription(previousDescription);
    }
  };

  if (getting) return <></>;

  return (
    <Wrapper>
      <OverlayRightPanel
        open={showUsersSidePanel}
        closeNav={() => setShowUsersSidePanel(false)}
        title="Update Team Members"
      >
        {showUsersSidePanel && (
          <AddUserForm closeMenu={() => setShowUsersSidePanel(false)} />
        )}
      </OverlayRightPanel>

      <Grid container rowSpacing={0}>
        <Grid xs={4} display="flex" justifyContent="center">
          <StyledH4>Productline Information</StyledH4>
        </Grid>
        <Grid xs={4} display="flex" justifyContent="center" xsOffset={1}>
          <StyledH4>Actions</StyledH4>
        </Grid>
        <Grid container xs={4} rowSpacing={0}>
          <Grid xs={12}>
            <Tooltip title={NAME_FIELD_TOOLTIP} arrow placement="right">
              <TooltipAnchor>
                <TextField
                  label="Name"
                  value={name}
                  id="productline-name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="normal"
                  onBlur={updateName}
                  onChange={handleNameChange}
                  disabled={!isAdmin}
                  inputProps={{ maxLength: MAX_PRODUCTLINE_NAME_LENGTH }}
                />
              </TooltipAnchor>
            </Tooltip>
          </Grid>
          <Grid xs={12}>
            <Tooltip title={EXPORT_NAME_FIELD_TOOLTIP} arrow placement="right">
              <TooltipAnchor>
                <TextField
                  label="Export Name"
                  value={exportName}
                  id="productline-export-name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="normal"
                  onBlur={updateExportName}
                  onChange={handleExportNameChange}
                  disabled={!isAdmin}
                  inputProps={{ maxLength: MAX_PRODUCTLINE_NAME_LENGTH }}
                />
              </TooltipAnchor>
            </Tooltip>
          </Grid>
          <Grid xs={12}>
            <Tooltip title={DESCRIPTION_FIELD_TOOLTIP} arrow placement="right">
              <TooltipAnchor>
                <TextField
                  label="Description"
                  value={description}
                  id="productline-description"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  margin="normal"
                  onBlur={updateDescription}
                  onChange={handleDescriptionChange}
                  disabled={!isAdmin}
                />
              </TooltipAnchor>
            </Tooltip>
          </Grid>
          <Grid xs={12}>
            <Tooltip title={REGION_FIELD_TOOLTIP} arrow placement="right">
              <TooltipAnchor>
                <TextField
                  label="Region"
                  value={region}
                  id="productline-region"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="normal"
                  select
                  SelectProps={{
                    native: true,
                  }}
                  onChange={handleRegionChange}
                  disabled={!isAdmin}
                >
                  {productRegionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </TooltipAnchor>
            </Tooltip>
          </Grid>
          <Grid xs={12}>
            <Tooltip title={ID_FIELD_TOOLTIP} arrow placement="right">
              <TooltipAnchor>
                <TextField
                  label="Id"
                  id="productline-id"
                  value={productLine?.id}
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="normal"
                  disabled
                />
              </TooltipAnchor>
            </Tooltip>
          </Grid>
          <Grid xs={12}>
            <Tooltip title={FAMILY_ID_FIELD_TOOLTIP} arrow placement="right">
              <TooltipAnchor>
                <TextField
                  label="Family Id"
                  id="productline-familyId"
                  value={productLine?.familyId}
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="normal"
                  disabled
                />
              </TooltipAnchor>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid container xs={4} rowSpacing={0} xsOffset={1}>
          <ActionStack groupTitle="" alignItems="stretch">
            <ValidateProductlineButton disabled={!isWritePriv} />
            <ShowValidationsButton />
            <ValidationSettingsButton disabled={!isAdmin} />
            <ExportProductlineToExcelButton />
            <ImportFromExcelButton />
            <Placeholder />
            <DeleteProductLineButton disabled={!isAdmin} />
          </ActionStack>
        </Grid>
        <Grid xs={4} display="flex" justifyContent="center">
          <StyledH4>Team Members</StyledH4>
        </Grid>
        <Grid
          xs={4}
          xsOffset={1}
          display="flex"
          alignItems="center"
          sx={{ px: 2 }}
        >
          <ActionButton
            variant="outlined"
            size="medium"
            color="primary"
            icon={EditIcon}
            iconOnly={false}
            fullWidth
            tooltipText="Add or remove users and change their roles. Admins only."
            tooltipArrow
            tooltipPlacement="right"
            onClick={() => setShowUsersSidePanel(true)}
            disabled={!isAdmin}
          >
            Update Team Members
          </ActionButton>
        </Grid>
        <Grid xs={12}>
          <UserSection>
            <UserCardWrapper>
              <UserList />
            </UserCardWrapper>
          </UserSection>
        </Grid>
      </Grid>
    </Wrapper>
  );
}

const Placeholder = styled.div`
  padding: 20px;
`;

const TooltipAnchor = styled.span`
  display: block;
  width: 100%;
`;

const StyledH4 = styled(H4)`
  color: ${(props) => props.theme.colors.muttedHeadlines};
  font-weight: 700;
`;

const Wrapper = styled.div`
  display: flex;
  //max-width: 1400px;
  justify-content: space-between;
  padding: 0 24px 24px 24px;
`;

const UserSection = styled.div`
  flex: 1;
  padding-left: 10px;
  padding-right: 10px;
`;

const UserCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
`;
